import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, TrendingUp, Users, Briefcase, Target, BarChart3, Shield, Lightbulb, MessageSquare, HelpCircle, CheckCircle } from 'lucide-react';

const IMG = {
  hero: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80',
  what: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80',
  services: 'https://images.unsplash.com/photo-1624555130581-1d9cca783bc0?w=600&q=80',
  outsourced: 'https://images.pexels.com/photos/6285087/pexels-photo-6285087.jpeg?auto=compress&w=600',
  salary: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600&q=80',
  hire: 'https://images.pexels.com/photos/9034791/pexels-photo-9034791.jpeg?auto=compress&w=600',
  responsibilities: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&w=600',
  rates: 'https://images.pexels.com/photos/7433869/pexels-photo-7433869.jpeg?auto=compress&w=600',
};

const NAV_LINKS = [
  { id: 'what-is', label: 'What is a Fractional CSO?' },
  { id: 'cso-services', label: 'Fractional CSO Services' },
  { id: 'how-help', label: 'How a Fractional CSO Can Help' },
  { id: 'outsourced', label: 'Outsourced CSO' },
  { id: 'advisory', label: 'Advisory CSO' },
  { id: 'salary', label: 'Fractional CSO Salary' },
  { id: 'rates', label: 'Fractional CSO Rates' },
  { id: 'responsibilities', label: 'CSO Responsibilities' },
  { id: 'hire', label: 'Hire a Fractional CSO' },
  { id: 'faqs', label: 'Fractional CSO FAQs' },
];

const FAQS = [
  { q: 'What is a fractional CSO?', a: 'A fractional Chief Sales Officer builds upon the natural synergy that exists between marketing and sales, while giving CEOs another boardroom ally to manage sales growth, customer success initiatives, sales development, and the overall revenue-generating operation.' },
  { q: 'How much does a fractional CSO cost?', a: 'The cost depends upon the level of experience of the executive, the nature of the engagement, and whether your outsourced executive will work part-time or full-time. Typically, the compensation will be commensurate with that of the average CSO\'s salary, but with scalability and flexibility that you wouldn\'t have with a full-time hire.' },
  { q: 'What does a fractional CSO do?', a: 'A fractional CSO works alongside the CEO and leadership team to develop and implement revenue growth plans, identify new market opportunities, and optimize current revenue streams. They bring a fresh perspective and outside knowledge to drive innovation, increase efficiency, and foster customer success.' },
  { q: 'How does a fractional CSO work?', a: 'A fractional CSO can work with a business to develop and implement a revenue growth plan, identify new market opportunities, and optimize current revenue streams. They are available to work in-person, virtually, or in a hybrid arrangement.' },
  { q: 'Do I need a fractional CSO?', a: 'For CEOs who need another boardroom ally to manage revenue growth, customer success initiatives, sales development, and the overall revenue-generating operation, a fractional CSO is an excellent choice — especially for startups and growth-stage companies.' },
  { q: 'What should I look for in a fractional CSO?', a: 'Look for a highly experienced and capable professional with deep SaaS expertise who can help your business achieve revenue goals and sustainable growth. They should be able to set goals, develop and implement strategies, and ensure plans are effectively executed.' },
  { q: 'What are the benefits of hiring a fractional CSO?', a: 'A fractional CSO delivers C-suite-level expertise and experience without the cost and commitment of a full-time employee. They can develop and implement a revenue growth plan, identify new market opportunities, optimize current revenue streams, and bring fresh perspective to drive innovation.' },
  { q: 'When should a startup bring in a Fractional CSO?', a: 'Startup companies should consider a Fractional CSO when they need to manage their go-to-market aspirations. An experienced fractional CSO brings decades of experience in your industry and knows how to deliver time-tested methodologies, processes, and results.' },
  { q: 'What are fractional CSO services?', a: 'A Fractional CSO delivers services around revenue growth, sales development, project management, growth strategies, GTM execution, and more. As a senior member of management, they are the CEO\'s ally in business development, opportunity management, and human capital management.' },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

export default function RevScalePage() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-accent mb-4">Avant-Garde Consulting</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6" data-testid="revscale-title">
            Fractional CSO | Fractional Chief Sales Officer
          </h1>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3">
              <p className="text-lg text-white/80 leading-relaxed mb-4">
                The <strong className="text-white">fractional Chief Sales Officer</strong> model has been a boon for companies looking for an immediate boost in C-level sales leadership without the price tag and headaches of finding a full-time executive. In a tight labour market and an even tighter budget environment, finding the right Sales rainmaker to deploy your GTM strategy can be a steep uphill climb.
              </p>
              <p className="text-lg text-white/75 leading-relaxed mb-8">
                At Avant-Garde, we offer fractional Chief Sales Officers who bring deep experience in revenue growth, sales team building, RevOps, and go-to-market execution. Our CSOs work alongside CEOs as boardroom allies to manage revenue growth, customer success initiatives, sales development, and the overall revenue-generating operation.
              </p>
              <Link to="/contact" data-testid="revscale-cta-btn"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-8 py-4 hover:bg-accent/90 hover-lift transition-all group">
                Find Your CSO <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="lg:col-span-2 hidden lg:block">
              <img src={IMG.hero} alt="Executive boardroom meeting" className="w-full rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* In-Page Navigation */}
      <section className="py-8 bg-background border-b border-border px-4 sm:px-6 lg:px-8" data-testid="page-nav">
        <div className="max-w-5xl mx-auto">
          <p className="text-sm font-semibold text-foreground mb-4">Navigate this page:</p>
          <div className="flex flex-wrap gap-2">
            {NAV_LINKS.map((link) => (
              <button key={link.id} onClick={() => scrollTo(link.id)}
                className="text-xs font-medium text-primary hover:text-accent border border-border rounded-full px-4 py-1.5 hover:border-accent transition-colors">
                {link.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* What Is A Fractional CSO */}
      <section id="what-is" className="py-20 bg-background px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2">
              <img src={IMG.what} alt="Fractional CSO collaboration" className="w-full rounded-2xl shadow-lg" />
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">What Is A Fractional CSO?</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                A <strong className="text-foreground">fractional Chief Sales Officer</strong> (CSO) is a strategic leader at the top of their game, available on a part-time basis to help a business grow its sales. A fractional CSO delivers C-suite-level expertise and experience without the cost and commitment of a full-time employee.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                A fractional CSO works with your business to develop and implement a revenue growth plan, identify new market opportunities, and optimise current revenue streams. Hiring a fractional CSO is a cost-effective way to drive sales growth and achieve your financial goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fractional CSO Services */}
      <section id="cso-services" className="py-20 bg-muted px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Fractional CSO Services</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                What should you expect when engaging our <strong className="text-foreground">fractional CSO services</strong>? We only retain battle-tested and experienced executives who work alongside CEOs to help set objectives, develop strategies, and ensure efficient deployment of plans that drive growth.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                A Fractional CSO delivers services around revenue growth, sales development, outsourced sales, project management, growth strategies, and more. As a senior member of management, they are your ally in business development and human capital management.
              </p>
            </div>
            <div className="lg:col-span-2">
              <img src={IMG.services} alt="CSO strategy session" className="w-full rounded-2xl shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* How A Fractional CSO Can Help */}
      <section id="how-help" className="py-20 bg-background px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-8">How A Fractional CSO Can Help</h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            Many businesses don't have the capital to bring in a high-priced, full-time CSO. They don't want to make a huge commitment, and they don't want to overhaul everything. Instead, they want strategic direction from someone who knows what they're talking about.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-8">
            With Avant-Garde, instead of bringing in a permanent executive at an executive salary, you hire a CSO who acts as a strategic partner to your business. You get the direction you need plus a wealth of experience — our CSOs come with significant experience in scaling SaaS companies, giving you peace of mind with proven knowledge and expertise.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-lg bg-primary text-white font-semibold px-8 py-4 hover:bg-primary/90 hover-lift transition-all group">
            Find Your CSO <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Outsourced CSO */}
      <section id="outsourced" className="py-20 bg-primary/5 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">Outsourced CSO</h2>
              <p className="text-lg text-accent font-semibold mb-6">Build a solid sales foundation and accelerate revenue growth</p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Your Avant-Garde CSO becomes a part-time executive addition to your staff, bringing a results-based perspective to implement your sales strategy, manage, mentor, and grow your sales team, and get your company on the growth path you envision.
              </p>
              <div className="space-y-6">
                {[
                  { icon: Briefcase, title: 'Full-time, Interim Executive', desc: 'Need a pinch hitter while your sales executive is out? Or need a more concerted, full-time resource to lead your sales team.' },
                  { icon: Target, title: 'Strategic Project Support', desc: 'For extra executive bandwidth or to pass off an entire strategic sales effort or RevOps project. Your CSO will drive results to ensure your vision for positioning your sales team for success is achieved.' },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="card-depth rounded-xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-foreground mb-1">{item.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="lg:col-span-2">
              <img src={IMG.outsourced} alt="Outsourced CSO team" className="w-full rounded-2xl shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Advisory CSO */}
      <section id="advisory" className="py-20 bg-background px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-3 text-center">Advisory CSO</h2>
          <p className="text-muted-foreground text-center mb-12 text-lg max-w-2xl mx-auto">Strategic advisory, coaching, and audit services to elevate your sales organisation.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Users, title: 'Coaching for Sales Development', desc: 'Maximise the impact of your sales resources with an experienced C-level executive coach to mentor your sales leaders.' },
              { icon: Shield, title: 'Advisory for CEO Support', desc: 'For business leaders who want to personally develop their market-orientation and sales effectiveness.' },
              { icon: Lightbulb, title: 'Strategic Planning Workshops', desc: 'Tap decades of experience for your strategic planning process. Half-day to 2-day sessions with quarterly checkpoints.' },
              { icon: BarChart3, title: 'Sales Audits', desc: 'Get an objective opinion about your current sales department, processes and initiatives. Clarity and actionable insights in a short timeframe.' },
              { icon: TrendingUp, title: 'Go-to-Market Strategies', desc: 'Competitor analysis and data-driven GTM strategies tailored to your business, covering market positioning, channels, and pipeline.' },
              { icon: MessageSquare, title: 'Revenue Operations', desc: 'Optimise your RevOps stack, sales processes, CRM implementation, and data-driven decision making across the revenue cycle.' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="card-depth rounded-2xl p-7">
                  <div className="w-12 h-1 bg-accent rounded-full mb-5" />
                  <Icon className="w-7 h-7 text-primary mb-4" />
                  <h3 className="text-lg font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reach Your Goals */}
      <section className="py-20 hero-gradient px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Reach Your Goals with a Fractional CSO</h2>
          <p className="text-lg text-white/80 leading-relaxed mb-4">
            Many organisations who struggle with sales go for short-term wins at the expense of long-term credibility, or they saddle themselves with expensive hires that hamper future planning. That all changes with Avant-Garde.
          </p>
          <p className="text-lg text-white/75 leading-relaxed">
            Now, you can benefit from decades of knowledge, all at a reasonable price and term. When you hire a fractional CSO, you make a wise investment that helps you today and tomorrow, without sacrificing your company's long-term objectives.
          </p>
        </div>
      </section>

      {/* Fractional CSO Salary */}
      <section id="salary" className="py-20 bg-background px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2">
              <img src={IMG.salary} alt="Executive leadership" className="w-full rounded-2xl shadow-lg" />
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Fractional CSO Salary</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Given the critical role a Chief Sales Officer plays in cultivating sales growth, this is the one position you shouldn't skimp on. The key challenge is to bring in the right talent — and with the time and resources it takes to conduct an extensive search, you may find yourself further behind the competition.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                That's why hiring a fractional CSO is an outstanding alternative. The salary depends upon the level of experience, the nature of the engagement, and whether your outsourced executive will work part-time or full-time. With Avant-Garde, you get compensation commensurate with average CSO salary, but with scalability and flexibility.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fractional CSO Rates */}
      <section id="rates" className="py-20 bg-muted px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Fractional CSO Rates</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                With experienced sales talent in high demand, CEOs may find it challenging to find the right Chief Sales Officer. This has sent the market for both full-time CSOs and independent sales consultants through the roof.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                That's why companies are turning to Avant-Garde, where they find a fractional CSO for a competitive monthly retainer that restores predictability to the hiring process. Rates vary depending upon the engagement, but you'll retain someone with unmatched experience who can scale based upon your needs.
              </p>
            </div>
            <div className="lg:col-span-2">
              <img src={IMG.rates} alt="Business strategy planning" className="w-full rounded-2xl shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* CSO Responsibilities */}
      <section id="responsibilities" className="py-20 bg-background px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2">
              <img src={IMG.responsibilities} alt="CSO responsibilities" className="w-full rounded-2xl shadow-lg" />
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Fractional CSO Responsibilities</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When hiring a fractional CSO from Avant-Garde, you work with a highly experienced professional who helps your business achieve sales goals and sustainable growth. The fractional CSO works alongside you to set goals, develop and implement strategies, and ensure effective execution.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Other responsibilities include expertise in revenue growth, sales development, project management, growth strategies, and more. As a senior member of your management team, the fractional CSO is a valuable ally in business development and human capital management — helping recruit sales team members and set key performance indicators.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hire a Fractional CSO */}
      <section id="hire" className="py-20 bg-primary/5 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Hire a Fractional CSO</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Hiring a chief sales officer to join your executive management team can be a long and tedious process. However, an alternative is to <strong className="text-foreground">hire a fractional CSO</strong> from Avant-Garde. These experienced sales executives are prepared to work as part of your senior leadership team and help your business achieve rapid growth, scale revenue, and achieve sales success.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                By hiring an Avant-Garde fractional CSO, you bring on a highly skilled executive sales consultant who is ready to work as your ally, contribute to your corporate culture, and provide C-suite insights that restore your focus on growth.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-lg bg-accent text-white font-semibold px-8 py-4 hover:bg-accent/90 hover-lift transition-all group">
                Find Your CSO <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="lg:col-span-2">
              <img src={IMG.hire} alt="Hire a fractional CSO" className="w-full rounded-2xl shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faqs" className="py-20 bg-background px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-12">Fractional CSO FAQs</h2>
          <div className="space-y-3">
            {FAQS.map((faq, idx) => (
              <div key={faq.q} className="border border-border rounded-xl bg-card shadow-sm overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="flex w-full items-center justify-between text-left py-5 px-5 text-foreground font-medium hover:text-primary transition-colors">
                  <span className="pr-4 text-sm">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 text-muted-foreground transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96 pb-5 px-5' : 'max-h-0'}`}>
                  <p className="text-muted-foreground leading-relaxed text-sm">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 cta-gradient animate-gradient" />
        <div className="absolute inset-0 hero-texture opacity-50" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to accelerate your revenue?</h2>
          <p className="text-lg text-white/75 mb-8 max-w-xl mx-auto">Get a fractional CSO who understands SaaS, GTM, and what it takes to build predictable revenue.</p>
          <Link to="/contact" data-testid="revscale-final-cta"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-10 py-4 hover:bg-accent/90 hover-lift group">
            Find Your CSO <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
